import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ChevronLeft, Layers } from 'lucide-react';

interface Project {
  id: string;
  title: string;
  color: string;
}

const projects: Project[] = [
  { id: 'ai-terminal-course', title: 'AI Terminal Course', color: '#6366F1' },
  { id: 'mcp-jest', title: 'MCP Jest', color: '#FF6B6B' },
  { id: 'omnimcp', title: 'OmniMCP', color: '#4ECDC4' },
  { id: 'agentx', title: 'AA Kit (AgentX)', color: '#8B5CF6' },
  { id: 'ai-ui-kit', title: 'AI UI Kit', color: '#EC4899' },
  { id: 'genai-hooks', title: 'GenAI Hooks', color: '#10B981' },
  { id: 'sundae', title: 'Sundae', color: '#F59E0B' },
  { id: 'ideabrew', title: 'IdeaBrew', color: '#14B8A6' },
  { id: 'inflash', title: 'InFlash', color: '#3B82F6' },
  { id: 'clarity', title: 'Clarity for Gmail', color: '#EF4444' },
  { id: 'sketchflow', title: 'SketchFlow', color: '#1A535C' },
  { id: 'ddiy', title: 'DDIY Agent Builder', color: '#FFE66D' },
  { id: 'mcp-boilerplate', title: 'MCP Boilerplate', color: '#FF9F1C' },
  { id: 'tgsd-ai', title: "The 'Get Shit Done' AI", color: '#7C3AED' },
];

interface ProjectNavigationProps {
  activeIndex: number;
  onNavigate: (index: number) => void;
}

const ProjectNavigation: React.FC<ProjectNavigationProps> = ({
  activeIndex,
  onNavigate
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsExpanded(false);
      }
    };

    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExpanded]);

  return (
    <div
      ref={containerRef}
      className="fixed z-40 right-4"
      style={{ top: '50%', transform: 'translateY(-50%)' }}
    >
      {/* Toggle Button */}
      <motion.button
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.5 }}
        onClick={() => setIsExpanded(!isExpanded)}
        className="absolute right-0 p-3 rounded-full backdrop-blur-md border transition-all duration-300 bg-zinc-900/10 hover:bg-zinc-900/20 border-zinc-300 text-zinc-700"
        style={{ top: '50%', transform: 'translateY(-50%)' }}
        aria-label={isExpanded ? "Collapse navigation" : "Expand navigation"}
      >
        <AnimatePresence mode="wait">
          {isExpanded ? (
            <motion.div
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <ChevronRight className="w-5 h-5" />
            </motion.div>
          ) : (
            <motion.div
              key="open"
              initial={{ rotate: 90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: -90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <Layers className="w-5 h-5" />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>

      {/* Navigation Panel */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 100 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="w-72 max-h-[70vh] overflow-hidden rounded-2xl backdrop-blur-xl border shadow-2xl bg-white/95 border-zinc-200"
          >
            {/* Header */}
            <div className="px-4 py-3 border-b border-zinc-200">
              <div className="flex items-center gap-2">
                <Layers className="w-4 h-4 text-zinc-500" />
                <span className="text-xs font-medium uppercase tracking-wider text-zinc-500">
                  Side Projects
                </span>
              </div>
            </div>

            {/* Scrollable List */}
            <div className="overflow-y-auto max-h-[calc(70vh-48px)] py-2 px-2">
              {projects.map((project, index) => {
                const isActive = index === activeIndex;

                return (
                  <motion.button
                    key={project.id}
                    onClick={() => onNavigate(index)}
                    whileHover={{ x: 4 }}
                    whileTap={{ scale: 0.98 }}
                    className={`w-full text-left px-3 py-2.5 rounded-xl transition-all duration-200 flex items-center gap-3 group ${
                      isActive ? 'bg-zinc-100' : 'hover:bg-zinc-50'
                    }`}
                  >
                    {/* Number Badge */}
                    <span
                      className={`flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold font-mono transition-colors ${
                        isActive
                          ? 'text-white'
                          : 'bg-zinc-100 text-zinc-500 group-hover:text-zinc-700'
                      }`}
                      style={isActive ? { backgroundColor: project.color } : undefined}
                    >
                      {String(index + 1).padStart(2, '0')}
                    </span>

                    {/* Title */}
                    <span className={`text-sm font-medium truncate transition-colors ${
                      isActive
                        ? 'text-zinc-900'
                        : 'text-zinc-600 group-hover:text-zinc-900'
                    }`}>
                      {project.title}
                    </span>

                    {/* Active Indicator */}
                    {isActive && (
                      <motion.div
                        layoutId="activeProjectIndicator"
                        className="ml-auto w-1.5 h-1.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: project.color }}
                      />
                    )}
                  </motion.button>
                );
              })}
            </div>

            {/* Footer with count and navigation */}
            <div className="px-4 py-3 border-t border-zinc-200 flex items-center justify-between">
              <span className="text-xs text-zinc-500">
                {activeIndex + 1} of {projects.length}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    const prevIndex = activeIndex > 0 ? activeIndex - 1 : projects.length - 1;
                    onNavigate(prevIndex);
                  }}
                  className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors hover:bg-zinc-100 text-zinc-500"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => {
                    const nextIndex = activeIndex < projects.length - 1 ? activeIndex + 1 : 0;
                    onNavigate(nextIndex);
                  }}
                  className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors hover:bg-zinc-100 text-zinc-500"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProjectNavigation;
